import { StoreState } from '@/store'
import {
  LevelingEmbed,
  LevelingEmbedRef,
  LevelingMilestone,
  LevelingMilestoneGroup,
  LevelingOverview,
  LevelingStepDefinition,
  LevelingTabDefinition
} from '@/types/leveling'
import { filterArray, filterObject, isNumberString, isValues, keyAs, keyIs } from '@/utils/validation'
import { SerializedEditorState } from 'lexical'
import { isArray, isBoolean, isNumber, isObject, isString } from 'lodash'

function validateRef(ref: unknown): LevelingEmbedRef | undefined {
  if (!isObject(ref)) return
  if (Array.isArray(ref)) {
    return {
      profile: isString(ref[0]) && ref[0] ? ref[0] : undefined,
      id: isNumberString(ref[1]) ? Number(ref[1]) : undefined,
      index: isNumberString(ref[2]) ? Number(ref[2]) : undefined
    }
  } else {
    return {
      profile: keyIs(ref, 'profile', isString) ? ref.profile || undefined : undefined,
      id: keyIs(ref, 'id', isNumber) ? ref.id : undefined,
      index: keyIs(ref, 'index', isNumber) ? ref.index : undefined
    }
  }
}

function validateEditorState(obj: unknown) {
  if (!isObject(obj) || !('root' in obj)) return
  return obj as SerializedEditorState
}

function validateMilestone(obj: unknown, items: StoreState['items']): LevelingMilestone | undefined {
  if (!isObject(obj) || !keyIs(obj, 'id', isString)) return
  return {
    id: obj.id,
    title: keyIs(obj, 'title', isString) ? obj.title : '',
    content: keyAs(obj, 'content', validateEditorState),
    equipmentItems: keyIs(obj, 'equipmentItems', isObject)
      ? filterObject(obj.equipmentItems, (v) => (isNumberString(v) && items[Number(v)] ? Number(v) : undefined))
      : undefined
  }
}

function validateMilestoneGroup(
  obj: unknown,
  items: StoreState['items']
): LevelingMilestone | LevelingMilestoneGroup | undefined {
  if (!isObject(obj) || !keyIs(obj, 'id', isString)) return
  if (keyIs(obj, 'items', isArray)) {
    return {
      id: obj.id,
      title: keyIs(obj, 'title', isString) ? obj.title : '',
      items: filterArray(obj.items, (v) => validateMilestone(v, items))
    }
  } else {
    return validateMilestone(obj, items)
  }
}

function validateTab(obj: unknown): LevelingTabDefinition | undefined {
  if (!isObject(obj) || !keyIs(obj, 'id', isString)) return
  return {
    id: obj.id,
    name: keyIs(obj, 'name', isString) ? obj.name : '',
    content: keyAs(obj, 'content', validateEditorState)
  }
}

function validatePob(obj: unknown): { label: string; link: string } | undefined {
  if (!isObject(obj)) return
  return {
    label: keyIs(obj, 'label', isString) ? obj.label : '',
    link: keyIs(obj, 'link', isString) ? obj.link : ''
  }
}

function validateStep(obj: unknown): LevelingStepDefinition | undefined {
  if (!isObject(obj)) return
  return {
    level: keyIs(obj, 'level', isNumber) ? obj.level : 0,
    skills: keyAs(obj, 'skills', validateRef),
    passives: keyAs(obj, 'passives', validateRef),
    ascendancy: keyAs(obj, 'ascendancy', validateRef)
  }
}

function validateOverview(obj: object): LevelingOverview {
  const result: LevelingOverview = {}
  if (keyIs(obj, 'title', isString)) result.title = obj.title
  if (keyIs(obj, 'icon', isString)) result.icon = obj.icon
  if (keyIs(obj, 'updatedAt', isString)) result.updatedAt = obj.updatedAt
  if (keyIs(obj, 'author', isString)) result.author = obj.author
  if (keyIs(obj, 'fullGuideLink', isString)) result.fullGuideLink = obj.fullGuideLink
  if (keyIs(obj, 'plannerId', isString)) result.plannerId = obj.plannerId
  if ('introduction' in obj) result.introduction = validateEditorState(obj.introduction)
  if (keyIs(obj, 'showcaseVideo', isString)) result.showcaseVideo = obj.showcaseVideo
  if (keyIs(obj, 'league', isString)) result.league = obj.league
  if ('ascendancy' in obj) result.ascendancy = validateRef(obj.ascendancy)
  if ('equipment' in obj) result.equipment = validateRef(obj.equipment)
  if ('progressionEquipment' in obj) result.progressionEquipment = validateRef(obj.progressionEquipment)
  if ('pantheon' in obj) result.pantheon = validateRef(obj.pantheon)
  if ('passives' in obj) result.passives = validateRef(obj.passives)
  if ('skills' in obj) result.skills = validateRef(obj.skills)
  if (keyIs(obj, 'bandits', isString)) result.bandits = obj.bandits
  if (keyIs(obj, 'buildRating', isArray)) {
    result.buildRating = filterArray(obj.buildRating, (v) => {
      if (!isObject(v)) return
      return {
        title: keyIs(v, 'title', isString) ? v.title : '',
        subtitle: keyIs(v, 'subtitle', isString) ? v.subtitle : '',
        difficulty: keyIs(v, 'difficulty', isNumberString) ? Number(v.difficulty) : 1
      }
    })
  }
  if (keyIs(obj, 'leagueRating', isArray)) {
    result.leagueRating = filterArray(obj.leagueRating, (v) => {
      if (!isObject(v)) return
      return {
        slug: keyIs(v, 'slug', isString) ? v.slug : '',
        link: keyIs(v, 'link', isString) ? v.link : undefined,
        difficulty: keyIs(v, 'difficulty', isNumberString) ? Number(v.difficulty) : 1
      }
    })
  }
  if (keyIs(obj, 'strengthsAndWeaknesses', isObject)) {
    result.strengthsAndWeaknesses = {
      strengths: keyIs(obj.strengthsAndWeaknesses, 'strengths', isArray)
        ? filterArray(obj.strengthsAndWeaknesses.strengths, (v) => (isString(v) ? v : undefined))
        : [],
      weaknesses: keyIs(obj.strengthsAndWeaknesses, 'weaknesses', isArray)
        ? filterArray(obj.strengthsAndWeaknesses.weaknesses, (v) => (isString(v) ? v : undefined))
        : []
    }
  }
  if (keyIs(obj, 'craftingGuides', isArray)) {
    result.craftingGuides = filterArray(obj.craftingGuides, (v) => {
      if (!isObject(v) || !keyIs(v, 'id', isString)) return undefined
      return { id: v.id }
    })
  }
  if (keyIs(obj, 'strategies', isArray)) {
    result.strategies = filterArray(obj.strategies, (v) => {
      if (!isObject(v) || !keyIs(v, 'id', isString)) return undefined
      return { id: v.id }
    })
  }
  return result
}

export function validateLevelingEmbed(embed: unknown, items: StoreState['items']): LevelingEmbed | undefined {
  if (!isObject(embed) || !keyIs(embed, 'type', isValues('leveling'))) return
  if (!keyIs(embed, 'id', isNumberString)) return

  const result: LevelingEmbed = {
    id: Number(embed.id),
    name: keyIs(embed, 'name', isString) ? embed.name : 'Embed',
    type: embed.type,
    overview: keyIs(embed, 'overview', isObject) ? validateOverview(embed.overview) : {},
    steps: keyIs(embed, 'steps', isArray) ? filterArray(embed.steps, validateStep) : [],
    levelingSteps: keyIs(embed, 'levelingSteps', isArray)
      ? filterArray(embed.levelingSteps, (v) => {
          if (!isObject(v) || !keyIs(v, 'id', isString)) return
          return {
            id: v.id,
            text: keyIs(v, 'text', isString) ? v.text : ''
          }
        })
      : [],
    tabs: keyIs(embed, 'tabs', isArray) ? filterArray(embed.tabs, validateTab) : [],
    extraTabs: keyIs(embed, 'extraTabs', isArray) ? filterArray(embed.extraTabs, validateTab) : [],
    pobs: keyIs(embed, 'pobs', isArray) ? filterArray(embed.pobs, validatePob) : []
  }
  if (keyIs(embed, 'milestones', isArray)) {
    result.milestones = filterArray(embed.milestones, (v) => validateMilestoneGroup(v, items))
  }
  if (keyIs(embed, 'pobLink', isString)) result.pobLink = embed.pobLink
  if (keyIs(embed, 'showLevelingSteps', isBoolean)) result.showLevelingSteps = embed.showLevelingSteps
  if (keyIs(embed, 'showOverview', isBoolean)) result.showOverview = embed.showOverview
  if (keyIs(embed, 'showProgression', isBoolean)) result.showProgression = embed.showProgression
  if (keyIs(embed, 'showTipsAndTricks', isBoolean)) result.showTipsAndTricks = embed.showTipsAndTricks
  if ('tipsAndTricks' in embed) result.tipsAndTricks = validateEditorState(embed.tipsAndTricks)
  if ('skillGemsDescription' in embed) result.skillGemsDescription = validateEditorState(embed.skillGemsDescription)

  return result
}
